<template>
  <div class="relative w-full">
    <div class="relative">
      <div
        ref="contentRef"
        :style="containerStyle"
        class="w-full transition-[max-height] duration-500 overflow-hidden"
        @transitionend="onTransitionEnd"
      >
        <slot />
      </div>

      <div
        v-if="shouldFold"
        :class="[
          'pointer-events-none absolute bottom-0 transition-all  left-0 w-full bg-gradient-to-t from-surface-light to-transparent',
          open ? 'h-0' : 'h-24',
        ]"
      />
    </div>

    <BaseButton v-if="shouldFold" @click="toggle" class="mt-4 mb-4" color="default">
      <span>{{ $t(open ? 'common.show_less' : 'common.show_more') }}</span>
      <DropdownIcon
        :class="['w-4 h-4 ml-2 transition-transform', open ? 'rotate-180' : '']"
      />
    </BaseButton>
  </div>
</template>

<script setup lang="ts">
  import { computed,nextTick, onMounted, onUnmounted, ref, watch } from 'vue';

  import DropdownIcon from '../icons/DropdownIcon.vue';
  import BaseButton from './BaseButton.vue';

  const targetHeight = 325;
  const targetHeightString = `${targetHeight}px`;

  const open = ref(false);
  const shouldFold = ref(false);
  const contentRef = ref<HTMLElement | null>(null);
  const animating = ref(false);
  const maxHeight = ref(targetHeightString);

  function checkHeight() {
    if (contentRef.value) {
      shouldFold.value = contentRef.value.scrollHeight > targetHeight;
    }
  }

  function toggle() {
    if (!open.value) {
      animating.value = true;
      maxHeight.value = contentRef.value
        ? contentRef.value.scrollHeight + 'px'
        : 'none';
      open.value = true;
    } else {
      animating.value = true;
      if (contentRef.value) {
        maxHeight.value = contentRef.value.scrollHeight + 'px';
        nextTick(() => {
          maxHeight.value = targetHeightString;
        });
      } else {
        maxHeight.value = targetHeightString;
      }

      open.value = false;
    }
  }

  function onTransitionEnd() {
    if (open.value) {
      maxHeight.value = 'none';
      animating.value = false;
    } else {
      animating.value = false;
      open.value = false;
    }
  }

  const containerStyle = computed(() => {
    if (!shouldFold.value) return {};
    return {
      maxHeight:
        open.value || animating.value ? maxHeight.value : targetHeightString,
    };
  });

  onMounted(() => {
    checkHeight();
    window.addEventListener('resize', checkHeight);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', checkHeight);
  });

  watch(open, async () => {
    await nextTick();
    checkHeight();
  });
</script>
